/**
 * Execution modes.
 *
 * Contract: docs/source-of-truth/worker-service-delivery-architecture.md
 * ("Work occurrence" and "Execution mode to quantity derivation").
 *
 * Principle 2 — separate service from execution mode. An item, voucher, group
 * attendance, case activity or installation milestone is not a home visit and
 * must not be fabricated as one to reuse the mobile visit screen.
 */

import type { OfficialSahUnitType } from '../sah-service-list';

// ---------------------------------------------------------------------------
// Modes
// ---------------------------------------------------------------------------

export const EXECUTION_MODES = [
  'INDIVIDUAL_VISIT',
  'MULTI_WORKER_VISIT',
  'GROUP_SESSION',
  'TRIP',
  'FULFILMENT',
  'VOUCHER_ARRANGEMENT',
  'EXPENSE_EVIDENCE',
  'CASE_ACTIVITY',
  'MILESTONE',
] as const;

export type ExecutionMode = (typeof EXECUTION_MODES)[number];

const EXECUTION_MODE_SET: ReadonlySet<string> = new Set(EXECUTION_MODES);

export function isExecutionMode(value: string | null | undefined): value is ExecutionMode {
  return typeof value === 'string' && EXECUTION_MODE_SET.has(value);
}

// ---------------------------------------------------------------------------
// Worker time
// ---------------------------------------------------------------------------

/**
 * Whether the mode produces rostered worker time.
 *
 * `OPTIONAL` exists for fulfilment: a meal round is driven by a rostered
 * worker, whereas a supplier-shipped consumable is not. Phase 0 decision 6
 * ("rostered worker time only when actually delivered") is expressed here.
 *
 * The roster may only create a worker occurrence for `REQUIRED` or `OPTIONAL`.
 */
export const WORKER_TIME_REQUIREMENTS = ['REQUIRED', 'OPTIONAL', 'NONE'] as const;

export type WorkerTimeRequirement = (typeof WORKER_TIME_REQUIREMENTS)[number];

const WORKER_TIME_BY_MODE: Readonly<Record<ExecutionMode, WorkerTimeRequirement>> = {
  INDIVIDUAL_VISIT: 'REQUIRED',
  MULTI_WORKER_VISIT: 'REQUIRED',
  GROUP_SESSION: 'REQUIRED',
  TRIP: 'REQUIRED',
  CASE_ACTIVITY: 'REQUIRED',
  FULFILMENT: 'OPTIONAL',
  VOUCHER_ARRANGEMENT: 'NONE',
  EXPENSE_EVIDENCE: 'NONE',
  MILESTONE: 'NONE',
};

export function workerTimeRequirement(mode: ExecutionMode): WorkerTimeRequirement {
  return WORKER_TIME_BY_MODE[mode];
}

export function canRosterWorkerOccurrence(mode: ExecutionMode): boolean {
  return workerTimeRequirement(mode) !== 'NONE';
}

/**
 * Modes that carry a clocked start/finish on the worker occurrence itself.
 * `CASE_ACTIVITY` records authorised activity time but is not a clocked visit,
 * and must never be written into the clocked visit ledger.
 */
const CLOCKED_MODES: ReadonlySet<ExecutionMode> = new Set([
  'INDIVIDUAL_VISIT',
  'MULTI_WORKER_VISIT',
  'GROUP_SESSION',
  'TRIP',
]);

export function isClockedMode(mode: ExecutionMode): boolean {
  return CLOCKED_MODES.has(mode);
}

// ---------------------------------------------------------------------------
// Unit-type compatibility
// ---------------------------------------------------------------------------

/**
 * Which catalogue unit types each mode can honestly measure.
 *
 * Grounded in `OFFICIAL_SAH_SERVICE_CATALOGUE`, not assumed:
 *   hours(38) trip(1) voucher(1) meal(1) item(6) bill(2).
 * Care management is billed per `bill`, which is why `CASE_ACTIVITY` accepts
 * `bill` as well as the `hours` used by personal-affairs assistance.
 */
const UNIT_TYPES_BY_MODE: Readonly<Record<ExecutionMode, readonly OfficialSahUnitType[]>> = {
  INDIVIDUAL_VISIT: ['hours'],
  MULTI_WORKER_VISIT: ['hours'],
  GROUP_SESSION: ['hours'],
  TRIP: ['trip'],
  FULFILMENT: ['item', 'meal'],
  VOUCHER_ARRANGEMENT: ['voucher'],
  EXPENSE_EVIDENCE: ['item'],
  CASE_ACTIVITY: ['bill', 'hours'],
  MILESTONE: ['item'],
};

export function unitTypesForMode(mode: ExecutionMode): readonly OfficialSahUnitType[] {
  return UNIT_TYPES_BY_MODE[mode];
}

export function isModeCompatibleWithUnitType(
  mode: ExecutionMode,
  unitType: OfficialSahUnitType,
): boolean {
  return UNIT_TYPES_BY_MODE[mode].includes(unitType);
}

// ---------------------------------------------------------------------------
// Quantity derivation
// ---------------------------------------------------------------------------

/**
 * The operational fact a worker records, and the server-side derivation that
 * turns it into a claimable quantity.
 *
 * Principle 9 — the worker records real-world facts. No entry here permits a
 * frontline actor to author item, unit, price, contribution or funding source.
 */
export const QUANTITY_SOURCES = [
  'ELIGIBLE_ACTUAL_DURATION',
  'ALLOCATED_ACTUAL_DURATION',
  'PARTICIPANT_ALLOCATION',
  'COMPLETED_AUTHORISED_LEGS',
  'VERIFIED_ITEM_QUANTITY',
  'AUTHORISED_VOUCHER_UNITS',
  'APPROVED_RECEIPT_AMOUNT',
  'CADENCE_ENTITLEMENT',
  'APPROVED_MILESTONE_AMOUNT',
] as const;

export type QuantitySource = (typeof QUANTITY_SOURCES)[number];

const QUANTITY_SOURCE_BY_MODE: Readonly<Record<ExecutionMode, QuantitySource>> = {
  INDIVIDUAL_VISIT: 'ELIGIBLE_ACTUAL_DURATION',
  // Support ratio / allocation. Never duplicates the client service.
  MULTI_WORKER_VISIT: 'ALLOCATED_ACTUAL_DURATION',
  // Never clones the whole session amount per participant.
  GROUP_SESSION: 'PARTICIPANT_ALLOCATION',
  TRIP: 'COMPLETED_AUTHORISED_LEGS',
  FULFILMENT: 'VERIFIED_ITEM_QUANTITY',
  VOUCHER_ARRANGEMENT: 'AUTHORISED_VOUCHER_UNITS',
  // Eligible amount is derived from policy + approval + receipt; the submitter
  // does not choose claim price, category or funding source.
  EXPENSE_EVIDENCE: 'APPROVED_RECEIPT_AMOUNT',
  CASE_ACTIVITY: 'CADENCE_ENTITLEMENT',
  MILESTONE: 'APPROVED_MILESTONE_AMOUNT',
};

export function quantitySourceForMode(mode: ExecutionMode): QuantitySource {
  return QUANTITY_SOURCE_BY_MODE[mode];
}

/**
 * Modes whose quantity is an allocation across more than one subject, and which
 * therefore need an explicit no-double-claim assertion in the finance
 * projection (one client service, allocated once).
 */
const ALLOCATED_MODES: ReadonlySet<ExecutionMode> = new Set([
  'MULTI_WORKER_VISIT',
  'GROUP_SESSION',
]);

export function requiresAllocationInvariant(mode: ExecutionMode): boolean {
  return ALLOCATED_MODES.has(mode);
}
